import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Archive,
  X,
  Mail,
  Phone,
  FileText,
  Download,
  Repeat,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, API, formatPrice, formatDate, formatClassDate, formatTimeRange } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { ADMIN } from "@/constants/testIds";
import { Th, Td, CwStatusBadge } from "@/components/admin/shared/UIPrimitives";
import SeriesDialog from "@/components/admin/SeriesDialog";
import ExpenseEditor from "@/components/admin/ExpenseEditor";
import ProfitCard from "@/components/admin/ProfitCard";

export default function ClassesTable({ classes, onEdit, onNew, onRoster, refetch }) {
  const [seriesOpen, setSeriesOpen] = useState(false);
  const del = async (id) => {
    if (!confirm("Delete this class? This is permanent.")) return;
    try {
      await api.delete(`/admin/classes/${id}`);
      toast.success("Class deleted");
      refetch();
    } catch {
      toast.error("Could not delete");
    }
  };
  const archive = async (c) => {
    if (!confirm(`Archive "${c.title}"? It will hide from the dashboard but stay in the DB.`)) return;
    try {
      await api.patch(`/admin/classes/${c.id}`, { archived: true });
      toast.success("Class archived");
      refetch();
    } catch {
      toast.error("Archive failed");
    }
  };

  const exportCsv = () => {
    const headers = ["title", "class_date", "start_time", "end_time", "location", "price_student", "price_parent", "spots_filled", "capacity_total", "status", "series_id"];
    const rows = classes.map((c) => [
      c.title, c.class_date || "", c.start_time || "", c.end_time || "",
      c.location, c.price_student, c.price_parent,
      c.spots_filled || 0, c.capacity_total, c.status, c.series_id || "",
    ]);
    downloadCsv("classes", headers, rows);
  };

  return (
    <div>
      <div className="flex justify-end mb-4 gap-2 flex-wrap">
        <button
          onClick={exportCsv}
          className="btn-pill btn-outline"
          type="button"
          data-testid="admin-classes-export-csv"
        >
          <Download size={14} /> Export CSV
        </button>
        <button
          onClick={() => setSeriesOpen(true)}
          className="btn-pill btn-outline"
          type="button"
          data-testid="admin-create-series-btn"
        >
          <Repeat size={14} /> Create Series
        </button>
        <button onClick={onNew} className="btn-pill btn-primary" data-testid={ADMIN.newClassBtn}>
          <Plus size={14} /> New class
        </button>
      </div>
      {seriesOpen && (
        <SeriesDialog
          onClose={() => setSeriesOpen(false)}
          onCreated={() => { setSeriesOpen(false); refetch(); }}
        />
      )}
      <div className="rounded-2xl border border-gg-border bg-gg-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gg-bg text-xs uppercase tracking-[0.14em] text-gg-brown">
              <tr>
                <Th>Title</Th>
                <Th>Date</Th>
                <Th>Spots</Th>
                <Th>Roster</Th>
                <Th>Materials</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {classes.map((c) => {
                const totalChecklist = (c.materials_checklist || []).length;
                const doneChecklist = (c.materials_checklist || []).filter((m) => m.done).length;
                return (
                  <tr key={c.id} className="border-t border-gg-border align-top" data-testid={ADMIN.classRow(c.id)}>
                    <Td>
                      <div className="font-semibold">{c.title}</div>
                      <div className="text-xs text-gg-muted">{formatPrice(c.price_student)} student · {formatPrice(c.price_parent)} parent</div>
                      {c.admin_notes && (
                        <div className="text-[11px] text-gg-brown mt-1 italic max-w-[180px] line-clamp-2">📝 {c.admin_notes}</div>
                      )}
                    </Td>
                    <Td className="text-xs">
                      <div>{c.class_date ? formatClassDate(c.class_date) : formatDate(c.date_time_iso)}</div>
                      {formatTimeRange(c.start_time, c.end_time) && (
                        <div className="text-gg-muted mt-0.5">{formatTimeRange(c.start_time, c.end_time)}</div>
                      )}
                    </Td>
                    <Td>
                      <span className="font-semibold">{c.spots_filled || 0}</span>
                      <span className="text-gg-muted">/{c.capacity_total}</span>
                    </Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => onRoster(c)}
                        className="text-xs px-3 py-1 rounded-full bg-gg-bg border border-gg-border hover:border-gg-green hover:text-gg-green"
                        data-testid={`admin-roster-btn-${c.id}`}
                      >
                        View ({c.spots_filled || 0})
                      </button>
                    </Td>
                    <Td className="text-xs">
                      {totalChecklist === 0 ? (
                        <span className="text-gg-muted">—</span>
                      ) : (
                        <span className={doneChecklist === totalChecklist ? "text-gg-green font-semibold" : "text-[#9b6a26]"}>
                          {doneChecklist} / {totalChecklist} ready
                        </span>
                      )}
                    </Td>
                    <Td>
                      <span
                        className={`text-xs px-2.5 py-1 rounded-full ${
                          c.status === "open"
                            ? "bg-gg-green/15 text-gg-green"
                            : "bg-gg-muted/15 text-gg-muted"
                        }`}
                      >
                        {c.status}
                      </span>
                    </Td>
                    <Td>
                      <div className="flex gap-1.5 flex-wrap">
                        <button
                          type="button"
                          onClick={() => onEdit(c)}
                          className="text-gg-brown hover:text-gg-green p-1"
                          title="Edit class"
                          data-testid={`admin-edit-class-${c.id}`}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const { data } = await api.get(`/admin/classes/${c.id}/roster-url`);
                              window.open(`${API}${data.url}`, "_blank", "noopener");
                            } catch {
                              toast.error("Could not open roster");
                            }
                          }}
                          className="text-gg-brown hover:text-gg-green p-1"
                          title="Print roster (PDF)"
                          data-testid={`admin-roster-${c.id}`}
                        >
                          <FileText size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!confirm(`Send the reminder email + calendar invite to every registered parent for "${c.title}" right now?`)) return;
                            try {
                              const { data } = await api.post(`/admin/classes/${c.id}/send-reminder-now`, {});
                              toast.success(`Reminder sent to ${data.sent} parent${data.sent === 1 ? "" : "s"}`);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Send failed");
                            }
                          }}
                          className="text-gg-brown hover:text-gg-green p-1"
                          title="Send reminder email now"
                          data-testid={`admin-reminder-${c.id}`}
                        >
                          <Mail size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => archive(c)}
                          className="text-gg-muted hover:text-[#9b6a26] p-1"
                          title="Archive"
                          data-testid={`admin-archive-class-${c.id}`}
                        >
                          <Archive size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => del(c.id)}
                          className="text-[#B24C4C] hover:text-[#8a3434] p-1"
                          title="Delete (permanent)"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function ClassDialog({ cls, onClose, onSaved }) {
  const [form, setForm] = useState(cls);
  const [busy, setBusy] = useState(false);
  const isEdit = !!cls.id;

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: ["price_student", "price_parent", "capacity_total"].includes(k)
        ? Number(e.target.value)
        : e.target.value,
    }));

  const save = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/classes/${cls.id}`, form);
        toast.success("Class updated");
      } else {
        await api.post(`/admin/classes`, form);
        toast.success("Class created");
      }
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Save failed";
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-gg-surface max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {isEdit ? "Edit class" : "New class"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Label className="sm:col-span-2 block">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Title</span>
            <Input value={form.title} onChange={set("title")} className="mt-1.5" />
          </Label>
          <Label className="block sm:col-span-2">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Date (Arizona time)</span>
            <Input
              type="date"
              value={form.class_date || ""}
              onChange={set("class_date")}
              className="mt-1.5"
              data-testid="class-date-input"
            />
          </Label>
          <Label className="block">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Start time</span>
            <Input
              type="time"
              value={form.start_time || ""}
              onChange={set("start_time")}
              className="mt-1.5"
              data-testid="class-start-time-input"
            />
          </Label>
          <Label className="block">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">End time</span>
            <Input
              type="time"
              value={form.end_time || ""}
              onChange={set("end_time")}
              className="mt-1.5"
              data-testid="class-end-time-input"
            />
          </Label>
          <Label className="block sm:col-span-2">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Location</span>
            <Input value={form.location} onChange={set("location")} className="mt-1.5" />
          </Label>
          <Label className="block sm:col-span-2">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Event venue address (shown in confirmation emails)</span>
            <Input
              value={form.event_address || ""}
              onChange={set("event_address")}
              className="mt-1.5"
              placeholder="Leave blank to have the email say 'organizer will share closer to class day'"
              data-testid="class-event-address"
            />
            <p className="text-[11px] text-gg-muted italic mt-1">
              If set, this exact address appears in registration confirmation emails. If blank, the email tells parents the organizer will share the address closer to the date.
            </p>
          </Label>
          <Label className="block sm:col-span-2">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">What to bring / wear (auto-included in emails)</span>
            <Textarea
              value={form.what_to_bring || ""}
              onChange={set("what_to_bring")}
              rows={3}
              className="mt-1.5"
              placeholder="e.g. Closed-toe shoes, sun hat, refillable water bottle. Bring a folding lawn chair."
              data-testid="class-what-to-bring"
            />
            <p className="text-[11px] text-gg-muted italic mt-1">
              Appears in the confirmation email and the day-before reminder.
            </p>
          </Label>
          <Label className="block">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Student price ($)</span>
            <Input type="number" step="0.01" value={form.price_student} onChange={set("price_student")} className="mt-1.5" />
          </Label>
          <Label className="block">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Parent price ($)</span>
            <Input type="number" step="0.01" value={form.price_parent} onChange={set("price_parent")} className="mt-1.5" />
          </Label>
          <Label className="block">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Capacity</span>
            <Input type="number" value={form.capacity_total} onChange={set("capacity_total")} className="mt-1.5" />
          </Label>
          <Label className="block">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Status</span>
            <select value={form.status} onChange={set("status")} className="mt-1.5 w-full h-10 rounded-md border border-gg-border bg-gg-surface px-3 text-sm">
              <option value="open">open</option>
              <option value="closed">closed</option>
            </select>
          </Label>
          <Label className="sm:col-span-2 block">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Description (public)</span>
            <Textarea value={form.description} onChange={set("description")} className="mt-1.5" rows={3} />
          </Label>
          <Label className="sm:col-span-2 block">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Curriculum box (shown on invoice)</span>
            <Textarea value={form.curriculum_box} onChange={set("curriculum_box")} className="mt-1.5" rows={3} />
          </Label>
          <Label className="sm:col-span-2 block">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">
              ESA academic alignment <span className="text-gg-muted">(hidden — invoice only)</span>
            </span>
            <Textarea value={form.esa_alignment} onChange={set("esa_alignment")} className="mt-1.5" rows={4} />
          </Label>
          <Label className="sm:col-span-2 block">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">
              📝 Private admin notes
            </span>
            <Textarea
              value={form.admin_notes || ""}
              onChange={set("admin_notes")}
              className="mt-1.5"
              rows={3}
              placeholder="Notes only visible in the admin dashboard. Not shown to parents."
              data-testid="class-admin-notes"
            />
          </Label>
          <div className="sm:col-span-2">
            <MaterialsChecklistEditor
              items={form.materials_checklist || []}
              onChange={(items) => setForm((f) => ({ ...f, materials_checklist: items }))}
            />
          </div>
          <div className="sm:col-span-2 rounded-xl border border-gg-border bg-gg-bg p-4">
            <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold mb-3">
              Registration options (per class)
            </p>
            <div className="space-y-2 text-sm">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={form.allow_parent_addon !== false}
                  onChange={(e) => setForm((f) => ({ ...f, allow_parent_addon: e.target.checked }))}
                  className="mt-0.5"
                  data-testid="class-allow-parent-addon"
                />
                <span>
                  <strong>Allow parent add-on</strong>
                  <span className="block text-xs text-gg-muted">Parents can pay to join the class alongside their student (uses the parent price above).</span>
                </span>
              </label>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={form.allow_siblings !== false}
                  onChange={(e) => setForm((f) => ({ ...f, allow_siblings: e.target.checked }))}
                  className="mt-0.5"
                  data-testid="class-allow-siblings"
                />
                <span>
                  <strong>Allow additional siblings</strong>
                  <span className="block text-xs text-gg-muted">Parents can register multiple students in one checkout.</span>
                </span>
              </label>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={form.allow_chaperones !== false}
                  onChange={(e) => setForm((f) => ({ ...f, allow_chaperones: e.target.checked }))}
                  className="mt-0.5"
                  data-testid="class-allow-chaperones"
                />
                <span>
                  <strong>Allow free chaperones</strong>
                  <span className="block text-xs text-gg-muted">Adults are welcome to accompany their student without paying (they just watch/help).</span>
                </span>
              </label>
              <label className="flex items-start gap-3 pt-2 mt-2 border-t border-gg-border">
                <input
                  type="checkbox"
                  checked={form.direct_pay_only === true}
                  onChange={(e) => setForm((f) => ({ ...f, direct_pay_only: e.target.checked }))}
                  className="mt-0.5"
                  data-testid="class-direct-pay-only"
                />
                <span>
                  <strong>Direct Pay only (no ClassWallet)</strong>
                  <span className="block text-xs text-gg-muted">
                    Removes the payment-method step at checkout, hides all ClassWallet + ESA copy on the class page, and charges the full fee on a credit card. Use this for non-ESA-eligible offerings.
                  </span>
                </span>
              </label>
            </div>
          </div>
          {isEdit && cls.id && (
            <>
              <div className="sm:col-span-2">
                <ProfitCard kind="class" id={cls.id} />
              </div>
              <div className="sm:col-span-2">
                <ExpenseEditor scope="class" parentId={cls.id} />
              </div>
            </>
          )}
        </div>
        <DialogFooter className="mt-4">
          <button type="button" onClick={onClose} className="btn-pill btn-outline">
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="btn-pill btn-primary disabled:opacity-60"
            data-testid={ADMIN.saveClassBtn}
          >
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create class"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MaterialsChecklistEditor({ items, onChange }) {
  const [newLabel, setNewLabel] = useState("");
  const add = () => {
    if (!newLabel.trim()) return;
    onChange([...items, { label: newLabel.trim(), done: false }]);
    setNewLabel("");
  };
  const toggle = (i) => {
    const next = items.map((m, idx) => idx === i ? { ...m, done: !m.done } : m);
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="rounded-xl border border-gg-border bg-gg-bg p-4">
      <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold mb-3">
        🧺 Materials I need (private)
      </p>
      <div className="space-y-1.5">
        {items.length === 0 && (
          <p className="text-xs text-gg-muted italic">No items yet. Add your first one below.</p>
        )}
        {items.map((m, i) => (
          <div key={i} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => toggle(i)}
              className={`shrink-0 w-5 h-5 rounded border flex items-center justify-center text-[10px] ${
                m.done ? "bg-gg-green border-gg-green text-white" : "bg-gg-surface border-gg-border"
              }`}
              data-testid={`materials-toggle-${i}`}
            >
              {m.done ? "✓" : ""}
            </button>
            <span className={`flex-1 text-sm ${m.done ? "line-through text-gg-muted" : ""}`}>{m.label}</span>
            <button type="button" onClick={() => remove(i)} className="text-gg-muted hover:text-[#B24C4C]">
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Add a material… (e.g. 2 lbs flour, 10 mason jars)"
          className="text-sm"
          data-testid="materials-new-input"
        />
        <button type="button" onClick={add} className="btn-pill btn-outline shrink-0 text-xs px-3" data-testid="materials-add-btn">
          + Add
        </button>
      </div>
    </div>
  );
}

export function RosterDialog({ cls, onClose, onEditReg }) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["roster", cls.id],
    queryFn: async () => (await api.get(`/admin/classes/${cls.id}/roster`)).data,
  });
  const roster = data?.roster || [];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-gg-surface max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            Roster — {cls.title}
          </DialogTitle>
          <p className="text-sm text-gg-muted">
            {cls.class_date ? formatClassDate(cls.class_date) : ""}
            {formatTimeRange(cls.start_time, cls.end_time) ? ` · ${formatTimeRange(cls.start_time, cls.end_time)}` : ""}
            {" · "}{roster.length} / {cls.capacity_total} spots filled
          </p>
        </DialogHeader>
        {isLoading ? (
          <div className="p-8 text-center text-gg-muted">Loading roster…</div>
        ) : roster.length === 0 ? (
          <div className="p-8 text-center text-gg-muted">No students registered for this class yet.</div>
        ) : (
          <div className="border border-gg-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gg-bg text-xs uppercase tracking-[0.14em] text-gg-brown">
                <tr><Th>Student</Th><Th>Parent</Th><Th>Contact</Th><Th>Status</Th><Th></Th></tr>
              </thead>
              <tbody>
                {roster.map((r) => (
                  <tr key={r.id} className="border-t border-gg-border align-top">
                    <Td>
                      <div className="font-semibold">{r.student_name}</div>
                      <div className="text-xs text-gg-muted">#{r.invoice_number}</div>
                    </Td>
                    <Td>{r.parent_name}</Td>
                    <Td className="text-xs">
                      <div className="flex items-center gap-1"><Mail size={12} className="text-gg-brown" /> {r.parent_email}</div>
                      <div className="flex items-center gap-1 mt-0.5"><Phone size={12} className="text-gg-brown" /> {r.parent_phone}</div>
                    </Td>
                    <Td className="text-xs">
                      <CwStatusBadge reg={r} />
                      {r.parent_attending && (
                        <div className={`mt-1 ${r.add_on_delivered ? "text-gg-green" : "text-[#9b6a26]"}`}>
                          {r.add_on_delivered ? "✓ Add-on" : "Add-on pending"}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => onEditReg(r)}
                        className="text-xs px-3 py-1 rounded-full bg-gg-bg border border-gg-border hover:border-gg-green hover:text-gg-green"
                      >
                        Edit
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter className="mt-4">
          <button type="button" onClick={() => refetch()} className="btn-pill btn-outline">Refresh</button>
          <button type="button" onClick={onClose} className="btn-pill btn-primary">Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
